import { useCallback, useEffect, useState } from "react";
import PageContainer from "../components/pageContainer/PageContainer";
import SettingsLayout from "../components/settings/SettingsLayout";
import StyledInput from "../components/styledInput/StyledInput";
import Banner from "../components/banner/banner";
import Button from "../components/button";
import useModal from "../components/modal/useModal";
import UseContext from "../hooks/useContext";
import { Pencil, Plus, TrashIcon } from "../assets/icons";
import useTitle from "../hooks/TitleManager";
import useIntegrations from "../hooks/useIntegrations";
import ApiDatabase from "../server";
import { SettingsSectorsListGhost } from "../components/ghost/SettingsAndEmployeesGhost";

const SectorRow = ({ sector, employees, readOnly, onChangeUsers, onEdit, onDelete }) => (
  <div className="flex w-full min-w-0 items-center justify-between gap-4 rounded-lg border px-4 py-3">
    <div className="min-w-0 flex-1 truncate font-medium text-gray-900">
      {sector.name}
    </div>
    <div className="flex min-w-0 items-center gap-6">
      <div className="min-w-0 max-w-[160px] flex-1 basis-[160px]">
        <StyledInput
          type="selectMultipleCheckbox"
          isSearchable={true}
          value={`${sector.userIds?.length || 0} ${
            (sector.userIds?.length || 0) === 1 ? "membre" : "membres"
          }`}
          list={(employees || []).map((employee) => ({
            value: employee._id,
            label: `${employee.firstname} ${employee.lastname}`.trim(),
            checked: (sector.userIds || []).some(
              (id) => String(id) === String(employee._id)
            ),
          }))}
          onChange={(updated) => onChangeUsers(sector, updated)}
        />
      </div>
      {!readOnly && (
        <div className="flex shrink-0 gap-1">
          <Button color="ICON" size="ICON" onClick={() => onDelete(sector)}>
            <TrashIcon wh={20} color="#374151" />
          </Button>
          <Button color="ICON" size="ICON" onClick={() => onEdit(sector)}>
            <Pencil wh={20} color="#374151" />
          </Button>
        </div>
      )}
    </div>
  </div>
);

const SettingsSectors = () => {
  useTitle({ title: "Paramètres - Secteurs" });
  const integrations = useIntegrations();

  const [sectors, setSectors] = useState({ workfully: [], peopulse: [], jobbiz: [] });
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toggle } = useModal();
  const { setModalData } = UseContext();

  const refresh = useCallback(() => {
    setIsLoading(true);
    ApiDatabase.getSectors(
      {},
      (data) => {
        setSectors(data?.grouped || { workfully: [], peopulse: [], jobbiz: [] });
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
  }, []);

  useEffect(() => {
    refresh();
    ApiDatabase.getCompaniesInfosEmployees(
      {},
      (data) => {
        const flat = [];
        const seen = new Set();
        for (const company of data?.companies || []) {
          for (const employee of company.employees || []) {
            const id = String(employee._id);
            if (!seen.has(id)) {
              seen.add(id);
              flat.push(employee);
            }
          }
        }
        setEmployees(flat);
      },
      () => {}
    );
  }, [refresh]);

  const handleOpenCreate = () => {
    setModalData({
      employees,
      onSuccess: () => {
        refresh();
      },
    });
    toggle("create_sector");
  };

  const handleOpenEdit = (sector) => {
    setModalData({
      sector,
      onSuccess: () => {
        refresh();
      },
      onDeleteRequest: (s) => {
        setTimeout(() => {
          setModalData({
            sector: s,
            onSuccess: () => {
              refresh();
            },
          });
          toggle("delete_sector");
        }, 0);
      },
    });
    toggle("edit_sector");
  };

  const handleOpenDelete = (sector) => {
    setModalData({
      sector,
      onSuccess: () => {
        refresh();
      },
    });
    toggle("delete_sector");
  };

  const patchSectorUserIdsInState = useCallback((sectorId, nextUserIds) => {
    setSectors((prev) => {
      const sources = ["workfully", "peopulse", "jobbiz"];
      let draft = null;
      for (const key of sources) {
        const list = prev[key];
        if (!Array.isArray(list)) continue;
        const idx = list.findIndex((s) => String(s._id) === String(sectorId));
        if (idx === -1) continue;
        if (!draft) draft = { ...prev };
        const copy = [...draft[key]];
        copy[idx] = { ...copy[idx], userIds: [...nextUserIds] };
        draft[key] = copy;
      }
      return draft || prev;
    });
  }, []);

  const handleChangeUsers = (sector, updated) => {
    const userIds = updated.filter((opt) => opt.checked).map((opt) => opt.value);
    const previousUserIds =
      sector.userIds != null ? [...sector.userIds] : [];

    patchSectorUserIdsInState(sector._id, userIds);

    ApiDatabase.putUpdateSector(
      { id: sector._id, userIds },
      (data) => {
        const saved = data?.sector?.userIds;
        if (saved !== undefined) {
          patchSectorUserIdsInState(sector._id, saved);
        }
      },
      () => {
        patchSectorUserIdsInState(sector._id, previousUserIds);
      },
    );
  };

  const integrationLabel = (source) =>
    source === "peopulse" ? "Peopulse" : source === "jobbiz" ? "Jobbiz" : "Workfully";

  return (
    <PageContainer type="settings_sectors">
      <SettingsLayout>
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between pl-4">
            <h1 className="ft-xl font-semibold text-gray-900">Secteurs</h1>
            <Button color="PRIMARY" size="SM" onClick={handleOpenCreate}>
              <div className="flex items-center gap-1">
                <Plus wh={20} color="#FFFFFF" />
                Créer
              </div>
            </Button>
          </div>

          {integrations.hasAny && (
            <Banner type="info" hideIcon>
              <div className="rounded-lg p-3">
                <div className="font-semibold text-gray-900">Intégrations</div>
                <div className="text-gray-700 ft-sm">
                  Les secteurs sont automatiquement importés depuis vos
                  intégrations. Si vous utilisez uniquement vos intégrations,
                  inutile d'en créer manuellement.
                </div>
              </div>
            </Banner>
          )}

          {/* Workfully section */}
          <section className="flex flex-col gap-3">
            {isLoading ? (
              <SettingsSectorsListGhost />
            ) : (sectors.workfully || []).length === 0 ? (
              <div className="rounded-lg border p-4">
                <div className="font-medium">Aucun secteur créé</div>
                <div className="ft-sm text-gray-500">
                  Vous n'avez pas encore créé de secteur.
                </div>
                <div className="mt-3">
                  <Button
                    color="PRIMARY"
                    size="SM"
                    onClick={handleOpenCreate}
                  >
                    <div className="flex items-center gap-1">
                      <Plus wh={20} color="#FFFFFF" />
                      Créer un secteur
                    </div>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto">
                {(sectors.workfully || []).map((sector) => (
                  <SectorRow
                    key={sector._id}
                    sector={sector}
                    employees={employees}
                    readOnly={false}
                    onChangeUsers={handleChangeUsers}
                    onEdit={handleOpenEdit}
                    onDelete={handleOpenDelete}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Integration sections */}
          {["peopulse", "jobbiz"].map((source) => {
            if (!integrations[source]) return null;
            const list = sectors[source] || [];
            return (
              <section key={source} className="flex flex-col gap-3">
                <h2 className="text-base font-semibold text-gray-900 pl-4">
                  {integrationLabel(source)}
                </h2>
                {list.length === 0 ? (
                  <div className="ft-sm text-gray-500 pl-4">
                    Aucun secteur synchronisé pour {integrationLabel(source)}.
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto">
                    {list.map((sector) => (
                      <SectorRow
                        key={sector._id}
                        sector={sector}
                        employees={employees}
                        readOnly
                        onChangeUsers={handleChangeUsers}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </SettingsLayout>
    </PageContainer>
  );
};

export default SettingsSectors;
